import { useLanguage } from '../../../hooks/useLanguage';
import { useProduct } from '../../../hooks/useProduct';
import { X } from 'lucide-react';

const koreanToGoPhoto = `${import.meta.env.VITE_CLOUDFRONT_URL}/
KOST-홈페이지-이미지_WearefromSeoul.png `;

const content = {
  en: {
    title: 'THE SEOUL ORIGINAL™',
    sections: [
      {
        title: '🔥 Seoul Street, Seoul Vibes, Seoul Taste',
        description:
          "Forget everything you think you know about Korean food. We're not here to play it safe. We're here to blow your mind with the REAL Seoul experience – raw, intense, addictive.",
      },
      {
        title: '🌃 Born in the Streets, Made for the World',
        description:
          "Late nights in Hongdae. Street vendors in Myeongdong. Underground food spots only locals know about. That's where KOST was born. In the excitement, the noise, the pure ENERGY of Seoul's food scene.",
      },
      {
        title: '💪 No Fake, No Compromise',
        description:
          'While others water down Korean flavors for "global taste," we do the opposite. We crank it UP. More gochujang. More fermentation. More of that Seoul fire that makes you sweat and smile at the same time.',
      },
      {
        title: '⚡ Seoul Speed, Global Reach',
        description:
          "Seoul moves FAST. So do we. Instant ramen that doesn't taste instant. Sauces that hit harder than a K-pop beat drop. Korean comfort food that's ready when you are – because hunger doesn't wait.",
      },
      {
        title: '✨ The Seoul Standard',
        description:
          "Every KOST product passes the ultimate test: Would this make a Seoul food critic nod in approval? If it doesn't have that authentic Seoul punch, it doesn't make it to your bowl.",
      },
      {
        title: '🚀 From Seoul With Fire',
        description:
          "We're not just selling Korean food. We're selling Korean ENERGY. That unstoppable Seoul spirit that turns every meal into a moment, every bite into an experience.",
        callout: 'Ready to taste Seoul? Welcome to KOST.',
      },
    ],
    footer: 'Seoul born. Seoul made. Seoul approved.',
  },
  fr: {
    title: 'THE SEOUL ORIGINAL™',
    sections: [
      {
        title: '🔥 Seoul Street, Seoul Vibes, Seoul Taste',
        description:
          "Oubliez tout ce que vous croyez savoir sur la cuisine coréenne. On n'est pas là pour jouer la sécurité. On est là pour vous exploser les papilles avec la VRAIE expérience Seoul – brute, intense, addictive.",
      },
      {
        title: '🌃 Né dans la Rue, Fait pour le Monde',
        description:
          "Nuits blanches à Hongdae. Vendeurs de rue à Myeongdong. Spots food underground que seuls les locaux connaissent. C'est là que KOST est né. Dans l'excitement, le bruit, l'ÉNERGIE pure de la scène food de Seoul.",
      },
      {
        title: '💪 Pas de Fake, Pas de Compromis',
        description:
          'Pendant que d\'autres diluent les saveurs coréennes pour le "goût global," nous, on fait l\'inverse. On pousse le curseur À FOND. Plus de gochujang. Plus de fermentation. Plus de ce feu de Seoul qui vous fait transpirer et sourire en même temps.',
      },
      {
        title: '⚡ Seoul Speed, Global Reach',
        description:
          "Seoul bouge VITE. Nous aussi. Des ramen instantanés qui n'ont pas le goût d'instantané. Des sauces qui frappent plus fort qu'un beat drop K-pop. De la comfort food coréenne prête quand vous l'êtes – parce que la faim n'attend pas.",
      },
      {
        title: '✨ Le Standard Seoul',
        description:
          "Chaque produit KOST passe le test ultime : Est-ce qu'un critique food de Seoul hocherait la tête d'approbation ? Si ça n'a pas ce punch authentique de Seoul, ça n'arrive pas dans votre bol.",
      },
      {
        title: '🚀 From Seoul With Fire',
        description:
          "On ne vend pas juste de la food coréenne. On vend de l'ÉNERGIE coréenne. Cet esprit Seoul indomptable qui transforme chaque repas en moment, chaque bouchée en expérience.",
        callout: 'Prêt à goûter Seoul ? Bienvenue chez KOST.',
      },
    ],
    footer: 'Seoul born. Seoul made. Seoul approved.',
  },
};

export default function KoreanToGo() {
  const { language } = useLanguage();
  const { setActiveNavPanel } = useProduct();
  const data = content[language];

  return (
    <div className="min-h-full bg-red-600 relative">
      {/* Close Button - Mobile Only */}
      <button
        onClick={() => setActiveNavPanel(null)}
        className="lg:hidden absolute top-4 right-4 z-50"
      >
        <X className="w-8 h-8 text-white" />
      </button>

      {/* Title */}
      <div className="px-4 md:px-6 pt-6 md:pt-8 pb-4">
        <h1 className="text-5xl md:text-7xl font-black text-white tracking-tight leading-tight">
          KOREA
        </h1>
        <h1 className="text-5xl md:text-7xl font-black text-white tracking-tight leading-tight">
          TO GO
        </h1>
      </div>

      {/* Header Image */}
      <div className="w-full px-4 md:px-6 py-4 md:py-6">
        <div className="relative w-full aspect-square md:aspect-auto overflow-hidden rounded-2xl">
          <img
            src={koreanToGoPhoto}
            alt="THE SEOUL ORIGINAL™"
            className="w-full h-full object-cover"
          />
        </div>
      </div>

      {/* Sections */}
      <div className="px-4 md:px-6 pb-4 md:pb-6">
        <div className="max-w-5xl mx-auto space-y-4">
          {data.sections.map((section, index) => (
            <div
              key={index}
              className="bg-white rounded-xl p-5 md:p-6 shadow-lg"
            >
              <h3 className="text-lg md:text-xl font-bold text-gray-800 mb-3">
                {section.title}
              </h3>
              <p className="text-sm md:text-base text-gray-700 leading-relaxed">
                {section.description}
              </p>
              {section.callout && (
                <p className="text-base md:text-lg font-bold text-gray-900 mt-4 leading-relaxed">
                  {section.callout}
                </p>
              )}
            </div>
          ))}

          {/* Footer */}
          <div className="text-center pt-6 pb-20 lg:pb-8">
            <p className="text-xl md:text-2xl font-bold text-white whitespace-pre-line">
              {data.footer}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
